import React, { useEffect, useState } from "react";

// External Components
import Modal from "react-bootstrap/Modal";

// Local Assets
import CloseIcon from "../../../../assets/icons/close.svg";

// Custom Components
import RequestDropdown from "../../../Request/Dropdown/RequestDropdown";

// Apollo Client
import { useQuery, gql, useMutation } from "@apollo/client";

// External Libraries
import { sort } from "fast-sort";

// Custom Components and Utilities
import { notify } from "../../../Notify/Notify";
import { useParams } from "react-router-dom";
import { GET_REQUEST_DETAILS_BY_ID } from "../../RequestDetailedView";
import { uploadClient } from "../../../..";

// Graphql Related
const GET_ALL_FOLDERS = gql`
  query GetAllFolders {
    foldersForUser {
      id
      title
      isArchive
      isRoot
      parentFolder {
        id
      }
    }
  }
`;
const MOVE_SUBMISSION_TO_FOLDER = gql`
  mutation MoveSubmissionToFolder(
    $documentRequestUserIds: [String]!
    $subFolderId: String!
  ) {
    moveSubmissionToFolder(
      documentRequestUserIds: $documentRequestUserIds
      subFolderId: $subFolderId
    ) {
      success
    }
  }
`;

// Style constants
const labelStyles = {
  fontWeight: 510,
  textTransform: "uppercase",
};

function MoveSubmissions({
  isOpen = false,
  onCloseHandler = () => console.log("No onclose handler provided"),
  selectedUserIds = [],
}) {
  const { requestId } = useParams();
  const [selectedRootFolder, setSelectedRootFolder] = useState("");
  const [selectedNonRootFolder, setSelectedNonRootFolder] = useState("");

  const { data } = useQuery(GET_ALL_FOLDERS, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const filteredRootFolders =
    data?.foldersForUser?.filter(
      (folder) => folder.isRoot && !folder.isArchive
    ) ?? [];
  const filteredNonRootFolders =
    data?.foldersForUser?.filter(
      (folder) =>
        !folder.isRoot &&
        folder.parentFolder.id === selectedRootFolder &&
        !folder.isArchive
    ) ?? [];

  const handleRootFolderSelect = (selectedRootFolderTitle) => {
    const selectedRootFolderObject = filteredRootFolders.find(
      (folder) => folder.title === selectedRootFolderTitle
    );
    setSelectedRootFolder(selectedRootFolderObject?.id || "");

    // Reset non-root folder selection when the root folder changes
    setSelectedNonRootFolder("");
  };

  const handleNonRootFolderSelect = (selectedNonRootFolderTitle) => {
    const selectedNonRootFolderObject = filteredNonRootFolders.find(
      (folder) => folder.title === selectedNonRootFolderTitle
    );
    setSelectedNonRootFolder(selectedNonRootFolderObject?.id || "");
  };

  const [movedSubmissions, { loading: isMovedSubmissionsLoading }] =
    useMutation(MOVE_SUBMISSION_TO_FOLDER, {
      client: uploadClient,
      refetchQueries: [
        { query: GET_REQUEST_DETAILS_BY_ID, variables: { id: requestId } },
      ],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Move Document Mutation Error:", error);
      },
      onCompleted: () => {
        notify(
          "Success",
          `Documents are successfully moved to ${getTitleById(
            filteredNonRootFolders,
            selectedNonRootFolder
          )}`,
          "success"
        );
        onCloseHandler();
      },
      awaitRefetchQueries: true,
    });

  const handleSaveClick = () => {
    // Create the object structure when the save button is clicked
    movedSubmissions({
      variables: {
        documentRequestUserIds: selectedUserIds,
        subFolderId: selectedNonRootFolder,
      },
    });
  };

  useEffect(() => {
    if (!isOpen) {
      if (selectedNonRootFolder !== "") {
        setSelectedNonRootFolder("");
      }
      if (selectedRootFolder !== "") {
        setSelectedRootFolder("");
      }
    }
  }, [isOpen, selectedNonRootFolder, selectedRootFolder]);

  return (
    <Modal
      show={isOpen}
      dialogClassName="new-design-v1 request-details-move-submission-container"
      centered
      onHide={onCloseHandler}
    >
      <Modal.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h4 className="modal-title">{"Move Submissions"}</h4>
        </div>
        <div className="d-flex align-items-center">
          <img
            className="close"
            src={CloseIcon}
            alt="closeIcon"
            onClick={onCloseHandler}
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column" style={{ gap: "32px" }}>
          <div>
            <label style={labelStyles} htmlFor="selectRootFolder">
              Select Root Folder
            </label>
            <RequestDropdown
              value={getTitleById(filteredRootFolders, selectedRootFolder)}
              options={sort(
                filteredRootFolders.map((folder) => folder.title)
              ).asc()}
              onChange={handleRootFolderSelect}
              optionsMaxHeight={200}
            />
          </div>
          <div>
            <label style={labelStyles}>Select Non-Root Folder</label>
            <RequestDropdown
              value={getTitleById(
                filteredNonRootFolders,
                selectedNonRootFolder
              )}
              options={sort(
                filteredNonRootFolders.map((folder) => folder.title)
              ).asc()}
              onChange={handleNonRootFolderSelect}
              optionsMaxHeight={200}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="submit"
          className="secondary-button mr-4"
          style={{ maxWidth: "90px" }}
          onClick={onCloseHandler}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="primary-button"
          style={{ maxWidth: "160px" }}
          onClick={handleSaveClick}
          disabled={isMovedSubmissionsLoading || selectedNonRootFolder === ""}
        >
          Move Submissions
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default MoveSubmissions;

// Helper functions

/**
 * Retrieves the title of a folder by its ID from an array of folders.
 *
 * @param {Array} folders - An array of folders containing objects with 'id' and 'title' properties.
 * @param {string} id - The ID of the folder to retrieve the title for.
 *
 * @returns {string} The title of the folder with the specified ID, or an empty string if not found.
 */
function getTitleById(folders, id) {
  const folder = folders.find((folder) => folder.id === id);
  return folder ? folder.title : "";
}
